import { COUNTDOWN_VARIANTS, STUDIO_THEMES, StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { KIT_ASSET_WORKING_STATUSES, SerializedKitAsset } from '../../../../redux/api/fymAPI.types';
import type { SerializedReel } from '../types';

/**
 * The work of one asset type of one song, for its Studio step-2 card (owner request 2026-10-06:
 * "1 making", "2 ready", "1 failed" as the confirmation that "Make it" worked). The numbers count
 * RUNS, not files: one click on the banners makes three files, and the card says "1 making".
 */
export interface StudioCardWork {
  making: number;
  ready: number;
  failed: number;
  /** 0..100, the mean render progress of the runs that are being made; null when none is. */
  progress: number | null;
  /** The newest ready run of the card: "Open" shows its result. */
  readyAsset: SerializedKitAsset | null;
  /** AI reel card only: the newest ready reel. */
  readyReel: SerializedReel | null;
}

const WORKING_REEL_STATUSES: ReadonlyArray<string> = ['queued', 'analysing', 'planning', 'rendering'];
const READY_REEL_STATUSES: ReadonlyArray<string> = ['ready', 'approved'];
const COUNTDOWN_STILLS: ReadonlyArray<string> = COUNTDOWN_VARIANTS;

/**
 * The key of the run that made an asset. An asset type with fixed outputs (the banners, the cover
 * sizes, the motion artwork) makes all of them in one run; the three countdown stills of a story
 * ("Announce") are one run too. Every other variant (a story or feed post moment, a countdown day)
 * is a run of its own.
 */
export function kitRunKey(asset: Pick<SerializedKitAsset, 'themeId' | 'variant'>): string {
  if (STUDIO_THEMES[asset.themeId].outputs.length > 1) {
    return asset.themeId;
  }
  if (asset.themeId === 'story' && COUNTDOWN_STILLS.includes(asset.variant)) {
    return 'story:announce';
  }
  return `${asset.themeId}:${asset.variant}`;
}

/** The current assets of the run of `asset` (the Studio result shows every file of the run). */
export function assetsOfRun(asset: SerializedKitAsset, assets: SerializedKitAsset[]): SerializedKitAsset[] {
  const key = kitRunKey(asset);
  const run = assets.filter((one) => one.supersededAt === null && kitRunKey(one) === key);
  return run.length === 0 ? [asset] : run;
}

const isWorkingAsset = (asset: SerializedKitAsset): boolean => KIT_ASSET_WORKING_STATUSES.includes(asset.status);

function emptyWork(): StudioCardWork {
  return { making: 0, ready: 0, failed: 0, progress: null, readyAsset: null, readyReel: null };
}

function newer<T extends { createdAt: string }>(known: T | null, candidate: T): T {
  return known === null || known.createdAt < candidate.createdAt ? candidate : known;
}

/** The progress of a working run: the render percent while it renders, else 0. */
function runProgress(run: SerializedKitAsset[]): number {
  const working = run.filter(isWorkingAsset);
  const sum = working.reduce((total, asset) => total + (asset.status === 'rendering' ? asset.progress : 0), 0);
  return working.length === 0 ? 0 : sum / working.length;
}

function addAssetWork(cards: Map<StudioThemeId, StudioCardWork>, assets: SerializedKitAsset[]): void {
  const runs = new Map<string, SerializedKitAsset[]>();
  assets
    .filter((asset) => asset.supersededAt === null)
    .forEach((asset) => {
      const key = kitRunKey(asset);
      runs.set(key, [...(runs.get(key) ?? []), asset]);
    });
  const progressSums = new Map<StudioThemeId, number>();
  runs.forEach((run) => {
    const [first] = run;
    if (first === undefined) {
      return;
    }
    const { themeId } = first;
    const work = cards.get(themeId) ?? emptyWork();
    // A run with one file still being made is "making"; one failed file makes the run "failed".
    if (run.some(isWorkingAsset)) {
      work.making += 1;
      progressSums.set(themeId, (progressSums.get(themeId) ?? 0) + runProgress(run));
    } else if (run.some((asset) => asset.status === 'failed')) {
      work.failed += 1;
    } else {
      work.ready += 1;
      run.forEach((asset) => {
        work.readyAsset = newer(work.readyAsset, asset);
      });
    }
    cards.set(themeId, work);
  });
  progressSums.forEach((sum, themeId) => {
    const work = cards.get(themeId);
    if (work !== undefined && work.making > 0) {
      work.progress = Math.round(sum / work.making);
    }
  });
}

/**
 * The AI reel card. Every reel is a run. A failed reel counts only while no newer reel is ready
 * or being made (the rule of the Work badge: a newer try closes the failure). A rejected reel
 * counts as nothing.
 */
function addReelWork(cards: Map<StudioThemeId, StudioCardWork>, reels: SerializedReel[]): void {
  const newestFirst = [...reels].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  const work = emptyWork();
  let progressSum = 0;
  let isFailureOpen = true;
  newestFirst.forEach((reel) => {
    if (WORKING_REEL_STATUSES.includes(reel.status)) {
      work.making += 1;
      progressSum += reel.status === 'rendering' ? reel.progress : 0;
      isFailureOpen = false;
    } else if (READY_REEL_STATUSES.includes(reel.status)) {
      work.ready += 1;
      work.readyReel = work.readyReel ?? reel;
      isFailureOpen = false;
    } else if (reel.status === 'failed' && isFailureOpen) {
      work.failed += 1;
    }
  });
  if (work.making > 0) {
    work.progress = Math.round(progressSum / work.making);
  }
  if (hasCardWork(work)) {
    cards.set('aiReel', work);
  }
}

/** Pure: the work of each asset type of the song, from the release kit answer of the song. */
export function studioCardWork(
  assets: SerializedKitAsset[],
  reels: SerializedReel[]
): ReadonlyMap<StudioThemeId, StudioCardWork> {
  const cards = new Map<StudioThemeId, StudioCardWork>();
  addAssetWork(cards, assets);
  addReelWork(cards, reels);
  return cards;
}

/** True when the card has something to show: the "Ready to make" chip then gives way. */
export function hasCardWork(work: StudioCardWork | undefined): work is StudioCardWork {
  return work !== undefined && work.making + work.ready + work.failed > 0;
}

/** True when any asset type of the song is being made: the safety poll runs only then. */
export function isAnyCardMaking(cards: ReadonlyMap<StudioThemeId, StudioCardWork>): boolean {
  return Array.from(cards.values()).some((work) => work.making > 0);
}
